import { useAtticActivityListQuery } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import usePaginator, {
  createPaginationState,
  PaginatorContext,
} from "@dashboard/hooks/usePaginator";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { useMemo } from "react";

import AtticActivityListPage from "../components/AtticActivityListPage/AtticActivityListPage";
import {
  atticActivityListUrl,
  type AtticActivityListUrlFilters,
  type AtticActivityListUrlQueryParams,
  getAtticActivityFilter,
} from "../urls";

export const PAGINATE_BY = 50;

export interface AtticActivityListViewProps {
  params: AtticActivityListUrlQueryParams;
}

export const AtticActivityListView = ({ params }: AtticActivityListViewProps) => {
  const navigate = useNavigator();
  const paginationState = createPaginationState(PAGINATE_BY, params);
  const filter = useMemo(() => getAtticActivityFilter(params), [params]);
  const { data, loading, error } = useAtticActivityListQuery({
    displayLoader: true,
    variables: { ...paginationState, filter },
  });
  const paginationValues = usePaginator({
    pageInfo: data?.atticActivity?.pageInfo,
    paginationState,
    queryString: params,
  });
  // A filter change starts over at the first page: the cursor belonged to
  // the old list.
  const changeFilters = (changes: Partial<AtticActivityListUrlFilters>) =>
    navigate(atticActivityListUrl({ ...params, ...changes, after: undefined, before: undefined }));

  return (
    <PaginatorContext.Provider value={paginationValues}>
      <AtticActivityListPage
        entries={loading && !data ? undefined : (mapEdgesToItems(data?.atticActivity) ?? [])}
        loadFailed={!data && !loading && !!error}
        filters={params}
        onSearchChange={query => changeFilters({ query: query || undefined })}
        onKindChange={kind => changeFilters({ kind })}
        onCreatedFromChange={createdFrom => changeFilters({ createdFrom })}
        onCreatedToChange={createdTo => changeFilters({ createdTo })}
        onSubjectChange={subject => changeFilters({ subject })}
      />
    </PaginatorContext.Provider>
  );
};

export default AtticActivityListView;
